import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PhoneInput from '../components/PhoneInput';
import Layout from '../components/Layout';
import { useTranslation } from 'react-i18next';

import BilingualField from '../components/common/BilingualField';
import { studentsAPI, attendanceAPI, uploadAPI, resolveImage, approvalsAPI } from '../utils/api';
import { generateAttendanceCSV, generateAttendancePDF } from '../utils/exportUtils';
import { tField } from '../utils/bilingualHelper';
import { ATTENDANCE_COLORS, getAttendanceBadgeClass } from '../utils/attendanceTheme';







/* Note Modal */
function NoteModal({ onClose, onSave }) {
  const [note, setNote]   = useState('');
  const [loading, setLoad]= useState(false);
  return (
    <div className="dash-modal-overlay">
      <div className="dash-modal">
        <div className="dash-modal-header">
          <div className="dash-modal-title">Add Tutor Note</div>
          <button className="dash-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="dash-modal-body">
          <div className="form-group">
            <label className="form-label-style">Note *</label>
            <textarea className="form-input-style" rows={5} placeholder="Write a note about this student's progress..." value={note} onChange={e => setNote(e.target.value)} autoFocus />
          </div>
        </div>
        <div className="dash-modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={async () => { setLoad(true); await onSave(note); setLoad(false); onClose(); }} disabled={loading || !note.trim()}>
            {loading ? 'Saving...' : 'Save Note'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* Edit Student Modal */
function EditStudentModal({ student, onClose, onSave }) {
  const [form, setForm] = useState({
    ...student,
    name: typeof student.name === 'string' ? { en: student.name, zh: '' } : (student.name || { en: '', zh: '' })
  });

  const [loading, setLoad] = useState(false);
  const [uploading, setUploading] = useState(false);



  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('photo', file);
    setUploading(true);
    try {
      const res = await uploadAPI.studentPhoto(formData);
      setForm(prev => ({ ...prev, photoUrl: res.data.url }));
    } catch (err) {
      alert('Photo upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="dash-modal-overlay">
      <div className="dash-modal" style={{ maxWidth: 500 }}> 
        <div className="dash-modal-header">
          <div className="dash-modal-title">Edit Profile</div>
          <button className="dash-modal-close" onClick={onClose}>✕</button> 
        </div>
        <div className="dash-modal-body">
          <div className="form-group mb-4" style={{ textAlign: 'center' }}>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img 
                src={form.photoUrl ? resolveImage(form.photoUrl) : 'https://ui-avatars.com/api/?name=' + encodeURIComponent(tField(form.name) || 'User') + '&background=7b1d3a&color=fff'} 
                alt="Profile" 
                style={{ width: 80, height: 80, borderRadius: '50%', objectFit: 'cover', border: '3px solid #eee' }} 
              />
              <label style={{ 
                position: 'absolute', bottom: 0, right: 0, background: 'var(--wine)', color: '#fff', 
                width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', 
                justifyContent: 'center', cursor: 'pointer', border: '2px solid #fff', fontSize: 14 
              }}>
                ✎
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhotoUpload} disabled={uploading} />
              </label>
            </div>
            {uploading && <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Uploading photo...</div>}
          </div>
          
          <div className="form-group mb-3">
            <label className="form-label-style">Full Name *</label>
            <BilingualField label="" value={form.name} onChange={(_, lang, val) => setForm(p => ({ ...p, name: { ...p.name, [lang]: val } }))} placeholder="e.g. John Doe" />
          </div>
          <div className="form-group mb-3">
            <label className="form-label-style">Email</label>
            <input className="form-input-style py-2" value={form.email || ''} onChange={e => setForm(p=>({...p,email:e.target.value}))} />
          </div>
          <div className="form-group mb-3">
            <label className="form-label-style">Mobile</label>
            <PhoneInput value={form.mobile || ''} onChange={val => setForm(p=>({...p,mobile:val}))} />
          </div>
        </div>
        <div className="dash-modal-footer">
          <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary btn-sm" onClick={async () => { setLoad(true); await onSave(form); setLoad(false); onClose(); }} disabled={loading || !form.name?.en}>
            {loading ? 'Saving...' : 'Save Profile'}
          </button>
        </div>
      </div>
    </div>
  );
}

const FEE_BADGE  = { Paid:'badge-green', Pending:'badge-orange', Overdue:'badge-red' };
const BADGE_META = {
  'Perfect Attendance': { icon:'✅', color:'#4caf86', bg:'rgba(76,175,134,.1)' },
  'Star Performer':     { icon:'⭐', color:'#c04060', bg:'rgba(201,151,58,.1)' },
  'Most Improved':      { icon:'📈', color:'#3b7dd8', bg:'rgba(59,125,216,.1)' },
};

// ==========================================
// Isolated Batch Monthly Attendance Component
// ==========================================
function BatchMonthlyAttendanceCard({ studentId, studentName, batch, payments = [], globalFeeStatus, badges = [] }) {
  const { t } = useTranslation();
  const [selMonth, setSelMonth] = useState('');
  const [months, setMonths] = useState([]);
  const [monthlyData, setMonthlyData] = useState(null);
  const [monthlyLoad, setMonthlyLoad] = useState(false);

  // Initialize months based on this specific batch's duration
  useEffect(() => {
    let startD = new Date();
    startD.setMonth(startD.getMonth() - 5);
    let endD = new Date();
    
    if (batch.startDate) startD = new Date(batch.startDate);
    if (batch.endDate) endD = new Date(batch.endDate);
    
    if (startD > endD) {
      const temp = startD; startD = endD; endD = temp;
    }
    
    const mList = [];
    let curr = new Date(startD.getFullYear(), startD.getMonth(), 1);
    const end = new Date(endD.getFullYear(), endD.getMonth(), 1);
    
    let count = 0;
    while (curr <= end && count < 36) {
      const yStr = curr.getFullYear();
      const mStr = String(curr.getMonth() + 1).padStart(2, '0');
      mList.push(`${yStr}-${mStr}`);
      curr.setMonth(curr.getMonth() + 1);
      count++;
    }
    mList.sort((a, b) => b.localeCompare(a));
    
    // Fallback if empty
    if (mList.length === 0) {
      const today = new Date();
      mList.push(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`);
    }
    
    setMonths(mList);
    const todayStr = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`;
    setSelMonth(mList.includes(todayStr) ? todayStr : mList[0]);
  }, [batch]);

  // Fetch attendance only for this batch
  useEffect(() => {
    if (!studentId || !selMonth || !batch._id) return;
    setMonthlyLoad(true);
    attendanceAPI.getStudentMonthly(studentId, selMonth, batch._id)
      .then(r => setMonthlyData(r.data))
      .catch(() => setMonthlyData(null))
      .finally(() => setMonthlyLoad(false));
  }, [studentId, selMonth, batch._id]);

  const monthLabel = key => {
    const [y, m] = key.split('-');
    return new Date(y, m - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
  };

  const batchData = monthlyData?.batches?.[0]; // Backend returns only this batch due to batchId param

  const handleDownloadMonth = (format) => {
    if (!batchData || !batchData.records) return alert('No records to download.');
    const records = batchData.records.map(r => ({
      Date: new Date(r.date).toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' }),
      Batch: batch.name,
      Status: r.status,
      Note: r.note || '',
      MarkedBy: typeof r.markedBy === 'object' ? r.markedBy?.name : r.markedBy || ''
    }));

    const stats = {
      scheduled: batchData.scheduledClasses,
      present: batchData.present,
      late: batchData.late,
      absent: batchData.absent,
      leave: batchData.leave || 0,
      makeup: batchData.makeup || 0
    };

    if (format === 'csv') {
      generateAttendanceCSV(records, `${studentName}_${selMonth}_Attendance`, stats);
    } else {
      generateAttendancePDF(records, `${studentName}_${selMonth}_Attendance`, studentName, monthLabel(selMonth), stats);
    }
  };

  return (
    <div className="dash-card mb-6">
      <div className="dash-card-header">
        <div className="dash-card-title">📅 {t('studentDetail.monthlyAttendance')} - {tField(batch.name)}</div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button onClick={() => handleDownloadMonth('csv')} style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', color: '#333' }}>CSV</button>
          <button onClick={() => handleDownloadMonth('pdf')} style={{ background: '#fff', border: '1px solid #ddd', borderRadius: '6px', padding: '4px 10px', fontSize: '11px', fontWeight: 600, cursor: 'pointer', color: '#7b1d3a' }}>PDF</button>
          <select
            className="form-input-style"
            style={{ padding: '4px 10px', fontSize: 13, width: 'auto', minWidth: 160 }}
            value={selMonth}
            onChange={e => setSelMonth(e.target.value)}
          >
            {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
          </select>
        </div>
      </div>

      <div style={{ padding: '0 24px 24px' }}>
        {/* Batch header (Renders instantly using passed-in batch prop) */}
        <div style={{
          background: 'linear-gradient(135deg, #7b1d3a 0%, #c04060 100%)',
          borderRadius: 14, padding: '12px 16px', marginBottom: 12, color: '#fff',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: 16
        }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 15 }}>{tField(batch.name)}</div>
            <div style={{ fontSize: 12, opacity: 0.8 }}>
              {t('studentDetail.level')}: {batch.level} · {(batch.days || []).join(', ')}
              {batch.startTime && batch.endTime ? ` · ${batch.startTime} - ${batch.endTime}` : ''}
            </div>
            {(batch.startDate || batch.endDate) && (
              <div style={{ fontSize: 11, opacity: 0.65, marginTop: 2 }}>
                Duration: {batch.startDate ? new Date(batch.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Ongoing'} - {batch.endDate ? new Date(batch.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'Ongoing'}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
            {(() => {
              const currMonthStr = selMonth ? monthLabel(selMonth) : '';
              const feeRecord = payments.find(p => 
                p.month === currMonthStr && 
                (!p.batch || String(p.batch) === String(batch._id) || String(p.batch?._id) === String(batch._id))
              );
              let badgeBg = 'rgba(255,255,255,0.3)';
              let feeText = 'FEE DUE';
              if (feeRecord) {
                if (feeRecord.verificationStatus === 'Approved') { badgeBg = '#4caf86'; feeText = 'PAID'; }
                else if (feeRecord.verificationStatus === 'Pending') { badgeBg = '#e07d2a'; feeText = 'PENDING'; }
                else if (feeRecord.verificationStatus === 'Rejected') { badgeBg = '#e05252'; feeText = 'REJECTED'; }
              } else {
                if (globalFeeStatus === 'Paid') { badgeBg = '#4caf86'; feeText = 'PAID'; }
                else if (globalFeeStatus === 'Pending') { badgeBg = '#e07d2a'; feeText = 'PENDING'; }
                else { badgeBg = 'rgba(0,0,0,0.2)'; feeText = 'FEE DUE'; }
              }
              return (
                <div style={{
                  background: badgeBg, borderRadius: 30,
                  padding: '3px 10px', fontWeight: 700, fontSize: 10, letterSpacing: 0.5
                }}>
                  {feeText}
                </div>
              );
            })()}
          </div>
        </div>

        {monthlyLoad ? (
          <div className="dash-loading" style={{ padding: '30px 0' }}>⏳ Loading {tField(batch.name)} attendance...</div>
        ) : !batchData ? (
          <div className="dash-empty" style={{ padding: '30px 0' }}>
            <div className="dash-empty-icon">📅</div>
            <div className="dash-empty-title">{t('studentDetail.noRecordsFor')} {monthLabel(selMonth)}</div>
            <p className="dash-empty-sub">{t('studentDetail.attendanceWillAppear')}</p>
          </div>
        ) : (
          <>
            {/* Stat pills */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))', gap: 8, marginBottom: 14 }}>
              {[
                { label: t('studentDetail.scheduled'), val: batchData.scheduledClasses, color: '#6c757d' },
                { label: t('studentDetail.present'),   val: batchData.present,          color: ATTENDANCE_COLORS.present },
                { label: t('studentDetail.late'),      val: batchData.late,             color: ATTENDANCE_COLORS.late },
                { label: t('studentDetail.absent'),    val: batchData.absent,           color: ATTENDANCE_COLORS.absent },
                { label: 'Leave',                      val: batchData.leave || 0,       color: ATTENDANCE_COLORS.leave },
                { label: 'Make-up',                    val: batchData.makeup || 0,      color: ATTENDANCE_COLORS.makeup },
              ].map(item => (
                <div key={item.label} style={{
                  background: '#f8f9fa', borderRadius: 10, padding: '10px 6px', textAlign: 'center',
                  border: `1px solid ${item.color}22`
                }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: item.color }}>{item.val}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{item.label}</div>
                </div>
              ))}
            </div>

            {/* Daily records table */}
            {batchData.records.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#bbb', padding: '12px', fontSize: 13 }}>
                {t('studentDetail.noIndividualRecords')}
              </div>
            ) : (
              <div style={{ marginTop: 24 }}>
                <div style={{ overflowX: 'auto' }}>
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Note</th>
                        <th>Marked By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batchData.records.map((r, i) => (
                        <tr key={i}>
                          <td style={{ color: '#555' }}>{new Date(r.date).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })}</td>
                          <td>
                            <span className={`badge ${getAttendanceBadgeClass(r.status)}`} style={{ textTransform: 'uppercase', fontSize: 10 }}>
                              {r.status}
                            </span>
                          </td>
                          <td style={{ color: '#777', maxWidth: 200 }} className="truncate" title={r.note}>{r.note || '-'}</td>
                          <td style={{ color: '#999', fontSize: 12 }}>{typeof r.markedBy === 'object' ? r.markedBy?.name : r.markedBy || t('studentDetail.system')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Batch Specific Badges */}
            {badges.length > 0 && (
              <div style={{ marginTop: 24, borderTop: '1px solid #eee', paddingTop: 24 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#333', marginBottom: 16 }}>🎖️ {t('studentDetail.badgesEarnedIn')} {tField(batch.name)}</div>
                <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                  {badges.map((bObj, i) => {
                    const badgeName = typeof bObj === 'string' ? bObj : bObj.name;
                    const meta = BADGE_META[badgeName] || { icon:'🎓', color:'#7a7068', bg:'#f4f4f4' };
                    return (
                      <div key={i} style={{ padding:'14px', background: meta.bg, border:`1px solid ${meta.color}22`, borderRadius:12, textAlign:'center', minWidth:100 }}>
                        <div style={{ width:40, height:40, borderRadius:'50%', background: meta.color+'22', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 8px', fontSize:20 }}>{meta.icon}</div>
                        <div style={{ fontSize:11, fontWeight:600, color: meta.color }}>{badgeName}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function StudentDetail() {
  const { t } = useTranslation();
  const { id }   = useParams();
  const navigate = useNavigate();
  const [student, setStudent]   = useState(null);
  const [loading, setLoading]      = useState(true);
  const [editing, setEditing]   = useState(false);
  const [noteModal, setNoteModal]= useState(false);
  const attSectionRef = useRef(null);

  const [selAttBatchId, setSelAttBatchId] = useState(null);

  const fetchStudent = useCallback(() => {
    studentsAPI.getById(id)
      .then(r => { setStudent(r.data); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchStudent(); }, [fetchStudent]);

  useEffect(() => {
    if (student && !selAttBatchId) {
      const batches = student.batchIds || (student.batch ? [student.batch] : []);
      if (batches.length > 0) {
        setSelAttBatchId(batches[0]._id);
      }
    }
  }, [student, selAttBatchId]);

  const handleUpdate = async (updatedData) => {
    try {
      await studentsAPI.update(id, updatedData);
      setEditing(false);
      fetchStudent();
    } catch (err) {
      console.error(err);
      alert('Failed to update student profile.');
    }
  };

  const handleNote = async note => {
    await studentsAPI.addNote(id, note);
    fetchStudent(); // Use fetchStudent() instead of setStudent to ensure consistency
  };

  const handleToggleChecklist = async idx => {
    const updated = (student.lessonChecklist || []).map((item, i) => 
      i === idx ? { ...item, completed: !item.completed } : item
    );
    await studentsAPI.update(id, { lessonChecklist: updated });
    fetchStudent();
  };

  if (loading) return <Layout title="Student"><div className="dash-loading">⏳ Loading...</div></Layout>;
  if (!student) return <Layout title="Student"><div className="dash-card"><div className="dash-empty"><div className="dash-empty-title">Student not found</div></div></div></Layout>;



  return (
    <Layout title={tField(student.name)}>
      {noteModal && <NoteModal onClose={() => setNoteModal(false)} onSave={handleNote} />}
      {editing && <EditStudentModal student={student} onClose={() => setEditing(false)} onSave={handleUpdate} />}

      <button className="btn btn-outline btn-sm mb-6" onClick={() => navigate(-1)}>{t('common.back', '← BACK')}</button>

      {/* Pending Withdrawal Notice Alert Banner */}
      {student.resignationNotice?.status === 'Pending' && (
        <div style={{
          background: '#fdf2e9',
          border: '1px solid #e0b48f',
          borderLeft: '6px solid #d35400',
          borderRadius: '12px',
          padding: '18px 24px',
          marginBottom: '24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '20px' }}>⚠️</span>
              <strong style={{ fontSize: '16px', color: '#a04000' }}>
                Pending Withdrawal Notice / 退學申請待審核
              </strong>
            </div>
            <div style={{ marginTop: '6px', fontSize: '14px', color: '#5e3008' }}>
              <strong>Effective Date / 生效日期:</strong> {student.resignationNotice.effectiveDate || 'Subsequent Period'}
              {student.resignationNotice.reason && (
                <span style={{ marginLeft: '12px' }}>
                  <strong>Reason / 原因:</strong> {tField(student.resignationNotice.reason)}
                </span>
              )}
            </div>
            {student.resignationNotice.parentNote && (
              <div style={{ marginTop: '4px', fontSize: '13px', color: '#7e4511', fontStyle: 'italic' }}>
                "{tField(student.resignationNotice.parentNote)}"
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              className="btn btn-sm"
              style={{ background: '#27ae60', color: '#fff', border: 'none', fontWeight: 600, padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}
              onClick={async () => {
                if (!window.confirm('Approve withdrawal notice for this student? / 確定要確認批准此學員的退學申請嗎？')) return;
                try {
                  await approvalsAPI.processWithdrawal(student._id, 'Approve');
                  alert('Withdrawal notice approved successfully.');
                  fetchStudent();
                } catch (e) {
                  alert(`Failed: ${e.response?.data?.message || e.message}`);
                }
              }}
            >
              ✅ Approve / 確認退學
            </button>
            <button
              className="btn btn-sm btn-outline"
              style={{ borderColor: '#c0392b', color: '#c0392b', fontWeight: 600, padding: '8px 16px', borderRadius: '6px', cursor: 'pointer' }}
              onClick={async () => {
                const note = prompt('Reason for rejection (optional) / 請輸入駁回原因（選填）:');
                if (note === null) return;
                try {
                  await approvalsAPI.processWithdrawal(student._id, 'Reject', note);
                  alert('Withdrawal notice rejected.');
                  fetchStudent();
                } catch (e) {
                  alert(`Failed: ${e.response?.data?.message || e.message}`);
                }
              }}
            >
              ❌ Reject / 駁回通知
            </button>
          </div>
        </div>
      )}

      {/* Cinematic Identity Banner — Refined Obsidian Edition */}
      <div style={{ 
        background: 'linear-gradient(135deg, #2b0d15 0%, #111 60%, #000 100%)', 
        borderRadius: 24, padding: '50px 20px', textAlign: 'center', marginBottom: 30,
        boxShadow: '0 20px 40px rgba(0,0,0,0.3)', position: 'relative', overflow: 'hidden',
        color: '#fff' 
      }}>
        {/* Top Actions */}
        <div style={{ position: 'absolute', top: 20, right: 20, zIndex: 20 }}>
          <button 
            className="btn btn-outline btn-sm" 
            style={{ borderColor: 'rgba(255,255,255,0.3)', color: '#fff', background: 'rgba(0,0,0,0.2)', backdropFilter: 'blur(4px)' }}
            onClick={() => setEditing(true)}
          >
            ✏️ {t('studentDetail.editInfo')}
          </button>
        </div>

        {/* Glow Effect Overlay */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '100%', background: 'radial-gradient(circle at top, rgba(123, 29, 58, 0.2) 0%, transparent 70%)', pointerEvents: 'none' }}></div>

        <div style={{ position: 'relative', zIndex: 10 }}>
          <div style={{ 
            display: 'inline-block', marginBottom: 20, position: 'relative',
            padding: 10, borderRadius: '50%', background: 'radial-gradient(circle, rgba(123, 29, 58, 0.3) 0%, transparent 70%)'
          }}>
            <div className="dash-avatar-lg" style={{ 
              width: 100, height: 100, fontSize: 36, margin: '0 auto',
              backgroundColor: 'var(--wine)',
              backgroundImage: student.photoUrl ? `url("${resolveImage(student.photoUrl)}")` : 'none', 
              backgroundSize: 'cover', backgroundPosition: 'center',
              border: '4px solid rgba(255,255,255,0.15)',
              boxShadow: '0 10px 25px rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff'
            }}>
              {!student.photoUrl && student.name?.[0]?.toUpperCase()}
            </div>
          </div>

          <h1 style={{ color: '#fff', fontSize: 36, fontWeight: 700, marginBottom: 8, letterSpacing: '-0.5px' }}>{tField(student.name)}</h1>
          {student.studentId && (
            <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 16, fontWeight: 500, marginBottom: 8 }}>
              {student.studentId}
            </div>
          )}

          {student.status === 'Withdrawn' && (
            <div style={{
              display: 'inline-block',
              padding: '4px 14px',
              borderRadius: '20px',
              background: 'rgba(231, 76, 60, 0.25)',
              border: '1px solid rgba(231, 76, 60, 0.5)',
              color: '#ff7675',
              fontWeight: 700,
              fontSize: '12px',
              letterSpacing: '1px',
              marginBottom: 10
            }}>
              WITHDRAWN / 已退學 {student.resignationEffectiveDate ? `(Effective: ${student.resignationEffectiveDate})` : ''}
            </div>
          )}

          <div style={{ marginTop: 28, fontSize: 10, color: 'rgba(255,255,255,0.3)', letterSpacing: 2, textTransform: 'uppercase', fontWeight: 600 }}>
            ETS ACADEMY · MEMBER SINCE {new Date(student.enrollmentDate || student.createdAt).getFullYear()}
          </div>
        </div>
      </div>



      {/* Enrollment & Schedule */}
      {(student.batchIds || (student.batch ? [student.batch] : [])).length > 0 && (
        <div className="dash-card mb-6" style={{ background: 'linear-gradient(135deg, var(--wine) 0%, #7a2a40 100%)', color: '#fff', border: 'none' }}>
          <div className="dash-card-header" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
            <div className="dash-card-title" style={{ color: '#fff' }}>📅 {t('studentDetail.enrollmentSchedule')}</div>
            <span className="badge badge-gold" style={{ fontSize: 10 }}>{(student.batchIds || []).length > 1 ? t('studentDetail.activeEnrollments') : t('studentDetail.activeEnrollment')}</span>
          </div>
          <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
            {(student.batchIds || (student.batch ? [student.batch] : [])).map((b, idx, arr) => (
              <div 
                key={b._id || idx}
                onClick={() => { 
                  setSelAttBatchId(b._id); 
                  attSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                style={{ 
                  display: 'flex', gap: 40, flexWrap: 'wrap',
                  borderBottom: idx < arr.length - 1 ? '1px dashed rgba(255,255,255,0.2)' : 'none',
                  paddingBottom: idx < arr.length - 1 ? 24 : 0,
                  cursor: 'pointer',
                  padding: '16px',
                  borderRadius: '12px',
                  background: selAttBatchId === b._id ? 'rgba(255,255,255,0.1)' : 'transparent',
                  transition: 'background 0.2s',
                  position: 'relative'
                }}
              >
                {selAttBatchId === b._id && (
                  <div style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', fontSize: 24, opacity: 0.8 }}>
                    👈
                  </div>
                )}
                <div style={{ flex: '1 1 200px' }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1 }}>{t('studentDetail.currentBatch')}</div>
                  <div style={{ fontSize: 20, fontWeight: 700 }}>{tField(b.name)}</div>
                  <div style={{ fontSize: 12, opacity: 0.8 }}>{t('studentDetail.level')}: {b.level}</div>
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1 }}>{t('studentDetail.weeklySchedule')}</div>
                  <div style={{ fontSize: 16, fontWeight: 600 }}>{b.days?.join(', ') || 'N/A'}</div>
                  <div style={{ fontSize: 14, opacity: 0.9 }}>{b.startTime && b.endTime ? `${b.startTime} - ${b.endTime}` : 'TBD'}</div>
                </div>
                <div style={{ flex: '1 1 200px' }}>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: 1 }}>{t('studentDetail.assignedTutor')}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                    {b.tutor?.photoUrl ? (
                      <img src={resolveImage(b.tutor.photoUrl)} style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '2px solid rgba(255,255,255,0.2)' }} alt="tutor" />
                    ) : (
                      <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 14 }}>
                        {tField(b.tutor?.name)?.[0]?.toUpperCase() || '?'}
                      </div>
                    )}
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700 }}>{tField(b.tutor?.name) || t('studentDetail.unassigned')}</div>
                      {b.tutor?.email && <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>{b.tutor.email}</div>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Profile Details */}
      <div className="grid-2">
        <div className="dash-card">
          <div className="dash-card-header">
            <div className="dash-card-title">👤 {t('studentDetail.studentInfo')}</div>
          </div>
          <div className="dash-list-item" style={{ border: 'none', padding: '12px 16px' }}>
            <div className="text-sm text-muted">{t('studentDetail.studentId')}:</div>
            <div className="text-sm font-600" style={{ marginLeft: 'auto' }}>
              {student.studentId || '—'}
            </div>
          </div>
          <div className="dash-list-item" style={{ border: 'none', padding: '12px 16px' }}>
            <div className="text-sm text-muted">{t('studentDetail.dateOfBirth')}:</div>
            <div className="text-sm font-600" style={{ marginLeft: 'auto' }}>
              {student.dob ? new Date(student.dob).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : '—'} 
              <span className="text-xs text-muted" style={{ marginLeft: 8 }}>
                ({student.dob ? (() => {
                  const today = new Date();
                  const birth = new Date(student.dob);
                  let age = today.getFullYear() - birth.getFullYear();
                  const m = today.getMonth() - birth.getMonth();
                  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
                  return age;
                })() : 0} yrs)
              </span>
            </div>
          </div>
          <div className="dash-list-item" style={{ border: 'none', padding: '12px 16px' }}>
            <div className="text-sm text-muted">{t('studentDetail.email')}:</div>
            <div className="text-sm font-600" style={{ marginLeft: 'auto' }}>
              {student.email || student.userId?.email || '—'}
            </div>
          </div>
          <div className="dash-list-item" style={{ border: 'none', padding: '12px 16px' }}>
            <div className="text-sm text-muted">{t('studentDetail.mobile')}:</div>
            <div className="text-sm font-600" style={{ marginLeft: 'auto' }}>
              {student.mobile || student.userId?.mobile || '—'}
            </div>
          </div>
          <div className="dash-list-item" style={{ border: 'none', padding: '12px 16px' }}>
            <div className="text-sm text-muted">{t('studentDetail.feeAmount')}:</div>
            <div className="text-sm font-700" style={{ marginLeft: 'auto', color: 'var(--wine)' }}>
              ${(student.feeAmount && student.feeAmount !== 0 ? student.feeAmount : (student.batch?.feeAmount || 0)).toLocaleString()} HKD / mo
            </div>
          </div>
          <div className="dash-list-item" style={{ border: 'none', padding: '12px 16px' }}>
            <div className="text-sm text-muted">{t('studentDetail.paymentStatus')}:</div>
            <div className="text-sm" style={{ marginLeft: 'auto' }}>
              <span className={`badge ${FEE_BADGE[student.feeStatus] || 'badge-muted'}`}>{student.feeStatus}</span>
            </div>
          </div>
          <div className="dash-list-item" style={{ border: 'none', padding: '12px 16px' }}>
            <div className="text-sm text-muted">{t('studentDetail.starRating')}:</div>
            <div className="text-sm font-700" style={{ marginLeft: 'auto', color: '#c04060' }}>
              {student.performanceRating ? `${student.performanceRating} ⭐` : 'N/A'}
            </div>
          </div>
        </div>

        <div className="dash-card">
          <div className="dash-card-header">
            <div className="dash-card-title">🛡️ {t('studentDetail.guardianEmergency')}</div>
          </div>
          {student.parentId ? (
            <div style={{ padding: '0 16px 16px' }}>
              <div style={{ fontWeight: 600 }}>{tField(student.parentId.name)}</div>
              <div className="text-sm text-muted">{student.parentId.relationToStudent}</div>
              <div className="text-sm mt-3">📞 {student.parentId.mobile}</div>
              <div className="text-sm">📧 {student.parentId.email}</div>
            </div>
          ) : (
            <div className="dash-empty" style={{ padding: 20 }}>{t('studentDetail.noGuardianLinked')}</div>
          )}
        </div>
      </div>

      {/* Purchased Merchandise */}
      {student.purchasedMerchandise?.length > 0 && (
        <div className="dash-card mb-6" style={{ borderLeft: '4px solid #c04060', marginTop: 24 }}>
          <div className="dash-card-header">
            <div className="dash-card-title">🛍️ {t('studentDetail.purchasedMerchandise')}</div>
          </div>
          <div style={{ display:'flex', flexWrap:'wrap', gap:16, padding:'16px' }}>
            {student.purchasedMerchandise.map((item, i) => (
              <div key={i} style={{ 
                border: '1px solid #eee', borderRadius: 12, padding: '12px', minWidth: 200, 
                display: 'flex', alignItems: 'center', gap: 12, background: '#fdfdfd'
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 8, background: '#f0f0f0',
                  backgroundImage: item.merchandise?.imageUrl ? `url("${resolveImage(item.merchandise.imageUrl)}")` : 'none',
                  backgroundSize: 'cover', backgroundPosition: 'center', flexShrink: 0
                }}>
                  {!item.merchandise?.imageUrl && <div style={{width:'100%', height:'100%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20}}>🛍️</div>}
                </div>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#333' }}>{tField(item.merchandise?.name) || 'Unknown Item'}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>
                    HK$ {item.priceAtPurchase}
                    {item.size && <span style={{ marginLeft: 8, padding: '2px 6px', background: '#eee', borderRadius: 4, fontSize: 10, fontWeight: 700, color: '#555' }}>{t('studentDetail.size')}: {item.size}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Month-Wise Attendance Section ───────────────────────────── */}
      <div ref={attSectionRef} style={{ paddingTop: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {(student.batchIds || (student.batch ? [student.batch] : []))
            .filter(batch => batch._id === selAttBatchId)
            .map((batch, idx) => (
              <BatchMonthlyAttendanceCard 
                key={batch._id || idx} 
                studentId={student._id} 
                studentName={tField(student.name)} 
                batch={batch} 
                payments={student.paymentLedger || []} 
                globalFeeStatus={student.feeStatus} 
                badges={(student.badges || []).filter(b => b.batchId && (b.batchId._id === batch._id || b.batchId === batch._id || tField(b.batchId.name) === tField(batch.name)))}
              />
          ))}
        </div>
      </div>

      {/* Tutor Notes */}
      <div className="dash-card mb-6">
        <div className="dash-card-header">
          <div className="dash-card-title">📝 {t('studentDetail.tutorNotes')}</div>
          <button className="btn btn-sm btn-outline" onClick={() => setNoteModal(true)}>{t('studentDetail.addNote')}</button>
        </div>
        {!student.tutorNotes?.length ? (
          <p className="text-sm text-muted" style={{ padding:'12px 0' }}>{t('studentDetail.noNotesYet')}</p>
        ) : (
          student.tutorNotes.slice(-5).reverse().map((n, i) => (
            <div key={i} style={{ padding:'12px 0', borderBottom:'1px solid #e0d8ce' }}>
              <div style={{ fontSize:12, color:'#7a7068', marginBottom:6 }}>
                {new Date(n.date).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
              </div>
              <div style={{ background:'#f4f4f4', padding:'10px 14px', fontSize:14, lineHeight:1.65, color:'#3d3730' }}>{n.note}</div>
            </div>
          ))
        )}
      </div>

      {/* Objectives */}
      {student.objectives?.length > 0 && (
        <div className="dash-card mb-6">
          <div className="dash-card-title mb-4">🎯 {t('studentDetail.personalObjectives')}</div>
          {student.objectives.map((obj, i) => (
            <div key={i} style={{ padding:'14px 0', borderBottom:'1px solid #e0d8ce' }}>
              <div className="flex-between mb-4">
                <div style={{ fontWeight:600, fontSize:14 }}>{obj.title}</div>
                <div style={{ fontWeight:700, color: obj.progress >= 75 ? '#4caf86' : obj.progress >= 50 ? '#c04060' : '#e07d2a' }}>{obj.progress}%</div>
              </div>
              {obj.verifyText && <div className="text-sm text-muted mb-4">{obj.verifyText}</div>}
              <div className="progress-bar">
                <div className="progress-fill" style={{ width:`${obj.progress}%`, background: obj.progress >= 75 ? '#4caf86' : obj.progress >= 50 ? '#c04060' : '#e07d2a' }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lesson Checklist */}
      {student.lessonChecklist?.length > 0 && (
        <div className="dash-card mb-6">
          <div className="dash-card-title mb-4">📋 {t('studentDetail.lessonChecklist')}</div>
          {student.lessonChecklist.map((item, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 0', borderBottom:'1px solid #e0d8ce', cursor:'pointer' }}
              onClick={() => handleToggleChecklist(i)}>
              <div style={{ width:28, height:28, borderRadius:'50%', border:`2px solid ${item.completed ? '#4caf86' : '#e0e0e0'}`, background: item.completed ? '#4caf86' : 'transparent', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:14, flexShrink:0, transition:'all .2s' }}>
                {item.completed ? '✓' : ''}
              </div>
              <span style={{ textDecoration: item.completed ? 'line-through' : 'none', color: item.completed ? '#7a7068' : '#111111', fontSize:14 }}>{item.item}</span>
            </div>
          ))}
        </div>
      )}

      {/* Authentic Parent Account (Linked Guardian) */}
      {student.parentId && (
        <div className="dash-card mb-6" style={{ borderLeft: '4px solid var(--gold)' }}>
          <div className="dash-card-header">
            <div className="dash-card-title">👨🏻‍💼 {t('studentDetail.authenticGuardian')}</div>
            <button className="btn btn-xs btn-primary" onClick={() => navigate('/parents')}>{t('studentDetail.manageGuardian')}</button>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:16, padding:'12px 0' }}>
            <div className="dash-avatar" style={{ background:'var(--off-white)', color:'var(--wine)', border:'1px solid var(--border)' }}>
              {tField(student.parentId.name)?.[0]}
            </div>
            <div>
              <div className="font-bold" style={{ fontSize:15 }}>{tField(student.parentId.name)}</div>
              <div className="text-sm text-muted">
                {student.parentId.mobile || t('studentDetail.noMobile')} · {student.parentId.email || t('studentDetail.noEmail')}
              </div>
              <div className="flex gap-2 mt-2">
                <span className={`badge ${student.parentId.status === 'inactive' ? 'badge-danger' : 'badge-success'}`} style={{ fontSize:10 }}>
                   {student.parentId.status === 'inactive' ? t('studentDetail.accountDeactivated') : t('studentDetail.accountActive')}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Guardians List */}
      {student.guardians?.length > 0 && (
        <div className="dash-card mb-6">
          <div className="dash-card-title mb-4">👨‍👩‍👧 {t('studentDetail.parentGuardian')}</div>
          {student.guardians.map((g, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:14, padding:'12px 0', borderBottom:'1px solid #e0d8ce' }}>
              <div className="dash-avatar" style={{ background:'rgba(123,29,58,.1)', borderColor:'rgba(123,29,58,.3)' }}>{tField(g.name)?.[0]?.toUpperCase()}</div>
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:600, fontSize:14 }}>{tField(g.name)}</div>
                <div className="text-sm text-muted">{g.relation}{g.mobile ? ` · ${g.mobile}` : ''}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Info */}
    </Layout>
  );
}
